// Staff/coach-facing credentials card on the athlete detail page.
// Read-only display grouped by credential_type; verified credentials show a blue
// seal. Staff / partners (perms.canVerify) get a toggle seal, coaches see it
// read-only — same rule as AccoladesCard / EventMarksCard. Equestrian-only.
import React from 'react';
import SealButton from './SealButton';
import useCredentialVerify from './useCredentialVerify';
import { CREDENTIAL_TYPE_LABEL, CREDENTIAL_TYPE_OPTIONS } from '../../lib/equestrian';

// Group credentials by credential_type, ordered to match the picklist order.
function groupByType(credentials) {
  const order = new Map(CREDENTIAL_TYPE_OPTIONS.map((o, i) => [o.value, i]));
  const byType = new Map();
  for (const c of credentials) {
    const t = c.credential_type || '';
    if (!byType.has(t)) byType.set(t, []);
    byType.get(t).push(c);
  }
  return [...byType.entries()]
    .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
    .map(([type, rows]) => ({ type, label: CREDENTIAL_TYPE_LABEL[type] || type || 'Other', rows }));
}

export default function CredentialsCard({ userId, credentials, setCredentials, canVerify, apiUrl, getConfig }) {
  const { toggleVerify, busyId } = useCredentialVerify({ userId, apiUrl, getConfig, setCredentials });
  if (!credentials || credentials.length === 0) return null;

  const groups = groupByType(credentials);

  return (
    <div className="ae-card ae-card--pad">
      <div className="ae-card__head">
        <h3 className="ae-card__title">Credentials</h3>
        {canVerify && <span className="ae-card__hint">Click a seal to verify</span>}
      </div>
      <div className="ae-marks">
        {groups.map(g => (
          <div key={g.type} className="ae-mark-group">
            <div className="ae-mark-group__head">{g.label}</div>
            {g.rows.map(c => (
              <div key={c.id} className="ae-accolade">
                <div className="ae-accolade__body">
                  <div className="ae-accolade__title">{c.title}</div>
                  <div className="ae-accolade__meta">
                    {c.issuer && <span className="ae-accolade__src">{c.issuer}</span>}
                    {c.status && <span className="accolade-chip">{c.status}</span>}
                    {c.identifier && <span className="ae-accolade__src">#{c.identifier}</span>}
                    {c.issued_date && <span className="ae-accolade__src">Issued {c.issued_date}</span>}
                    {c.expires_date && <span className="ae-accolade__src">Expires {c.expires_date}</span>}
                  </div>
                </div>
                {canVerify ? (
                  <SealButton
                    className="ae-accolade__seal"
                    state={c.verified ? 'verified' : 'unverified'}
                    title={c.verified ? 'Verified — click to unverify' : 'Not verified — click to verify'}
                    disabled={busyId === c.id}
                    onToggle={() => toggleVerify(c)}
                  />
                ) : (
                  c.verified && (
                    <SealButton className="ae-accolade__seal" state="verified" title="Verified" disabled onToggle={() => {}} />
                  )
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
